import { Badge, Card, EmptyState, ErrorState, Loading, formatDate } from "@corecrm/ui";
import { Link } from "react-router-dom";
import { useAllDeliverables, useMe, useProjects, useQuotes } from "../api/queries";
import { Status } from "../components/bits";
import { BILLING_MODEL } from "../lib/labels";

/** What an empty project list says (UX-198): a billing contact sees every project on the
    account; anyone else only the projects their project team put them on. */
export function noProjectsText(billing: boolean): string {
  return billing
    ? "No projects yet. Accept a quote to start one."
    : "No projects for you yet. You see the projects your project team adds you to; ask them if one is missing.";
}

/** One card per project, flagged when something awaits approval. The prototype's pProjects(). */
export function ProjectsPage() {
  const billing = useMe().data?.billing_contact ?? false;
  const projects = useProjects();
  const deliverables = useAllDeliverables(projects.data);
  const quotes = useQuotes();
  if (projects.isPending) return <Loading />;
  if (projects.error) return <ErrorState error={projects.error} onRetry={() => projects.refetch()} />;
  if (!projects.data.length) return <EmptyState>{noProjectsText(billing)}</EmptyState>;
  return (
    <>
      {projects.data.map((p) => {
        // deliverables and change requests waiting on the client (the prototype's `pend`)
        const waiting =
          deliverables.data.filter((d) => d.project_id === p.id && d.can_respond).length +
          (quotes.data ?? []).filter(
            (q) => q.kind === "change_request" && q.project_id === p.id && q.can_respond,
          ).length;
        return (
          <Card
            key={p.id}
            title={
              <div>
                <h2>
                  <Link to={`/projects/${p.id}`}>{p.name}</Link>
                </h2>
                <div className="faint">
                  {BILLING_MODEL[p.billing_model] ?? p.billing_model}
                  {p.started_at ? ` · started ${formatDate(p.started_at)}` : ""}
                  {p.status === "warranty" && p.warranty_ends_on
                    ? ` · warranty until ${formatDate(p.warranty_ends_on)}`
                    : ""}
                </div>
              </div>
            }
            action={
              <div className="row">
                {waiting > 0 && <Badge kind="acc">{waiting} awaiting your approval</Badge>}
                <Status status={p.status} />
              </div>
            }
          />
        );
      })}
    </>
  );
}
